// src/components/APanel/DistrictsCard.jsx
//
// Admin → Instrumente: sectoarele școlii și unde e poligon. Elevii dintr-un
// sector fără poligon fac primele lecții pe poligon; cu mai multe poligoane
// elevul alege o dată. Numele nu se schimbă după creare (lecțiile, instructorii
// și grupele păstrează sectorul ca text) — un sector nefolosit se dezactivează.

import React, { useCallback, useEffect, useState } from "react";

import {
   createDistrict,
   getDistricts,
   updateDistrict,
} from "../../api/districtsService";
import { reloadDistricts } from "../../hooks/useDistricts";

const NO_POLYGON_WARNING =
   "Nu va mai rămâne niciun poligon activ: elevii noi nu vor mai face lecții pe poligon. Continui?";

export default function DistrictsCard() {
   const [districts, setDistricts] = useState(null);
   const [busyId, setBusyId] = useState(null);
   const [newName, setNewName] = useState("");
   const [newHasPolygon, setNewHasPolygon] = useState(false);
   const [feedback, setFeedback] = useState(null);

   const load = useCallback(() => {
      getDistricts()
         .then((list) => setDistricts(Array.isArray(list) ? list : []))
         .catch((err) => setFeedback({ ok: false, text: err?.message }));
   }, []);

   useEffect(() => {
      load();
   }, [load]);

   const activePolygons = (districts || []).filter((d) => d.isActive && d.hasPolygon);

   const change = async (district, patch) => {
      const losesPolygon =
         district.isActive &&
         district.hasPolygon &&
         (patch.hasPolygon === false || patch.isActive === false);
      if (losesPolygon && activePolygons.length === 1 && !window.confirm(NO_POLYGON_WARNING)) {
         return;
      }
      setBusyId(district.id);
      setFeedback(null);
      try {
         const updated = await updateDistrict(district.id, patch);
         setDistricts((list) => list.map((d) => (d.id === district.id ? updated : d)));
         reloadDistricts().catch(() => {});
      } catch (err) {
         setFeedback({ ok: false, text: err?.message });
      } finally {
         setBusyId(null);
      }
   };

   const add = async (event) => {
      event.preventDefault();
      const name = newName.trim();
      if (!name) return;
      setBusyId("new");
      setFeedback(null);
      try {
         const created = await createDistrict({ name, hasPolygon: newHasPolygon });
         setDistricts((list) => [...(list || []), created]);
         setNewName("");
         setNewHasPolygon(false);
         setFeedback({ ok: true, text: `Sectorul „${created.name}” a fost adăugat.` });
         reloadDistricts().catch(() => {});
      } catch (err) {
         setFeedback({ ok: false, text: err?.message });
      } finally {
         setBusyId(null);
      }
   };

   return (
      <section className="adminOther__section">
         <div className="adminOther__sectionHead">
            <h2>Sectoare și poligoane</h2>
            <p>
               Sectoarele în care se programează elevii. Elevii dintr-un sector
               fără poligon fac primele lecții pe poligon; dacă sunt mai multe
               poligoane, elevul alege o dată. Numele nu se mai schimbă după
               adăugare — un sector nefolosit se dezactivează.
            </p>
         </div>

         <div className="bookingSettings districtsCard">
            {!districts && !feedback && (
               <p className="bookingSettings__muted">Se încarcă...</p>
            )}

            {districts && (
               <ul className="districtsCard__list">
                  {districts.map((d) => (
                     <li
                        key={d.id}
                        className={`districtsCard__row ${d.isActive ? "" : "districtsCard__row--inactive"}`}
                     >
                        <span className="districtsCard__name">{d.name}</span>
                        <label className="districtsCard__check">
                           <input
                              type="checkbox"
                              checked={Boolean(d.hasPolygon)}
                              disabled={busyId != null}
                              onChange={(e) => change(d, { hasPolygon: e.target.checked })}
                           />
                           Poligon
                        </label>
                        <label className="districtsCard__check">
                           <input
                              type="checkbox"
                              checked={Boolean(d.isActive)}
                              disabled={busyId != null}
                              onChange={(e) => change(d, { isActive: e.target.checked })}
                           />
                           Activ
                        </label>
                     </li>
                  ))}
               </ul>
            )}

            {districts && (
               <form className="districtsCard__add" onSubmit={add}>
                  <input
                     type="text"
                     className="bookingSettings__input districtsCard__input"
                     placeholder="Sector nou"
                     maxLength={60}
                     value={newName}
                     disabled={busyId != null}
                     onChange={(e) => setNewName(e.target.value)}
                     aria-label="Numele sectorului nou"
                  />
                  <label className="districtsCard__check">
                     <input
                        type="checkbox"
                        checked={newHasPolygon}
                        disabled={busyId != null}
                        onChange={(e) => setNewHasPolygon(e.target.checked)}
                     />
                     Poligon
                  </label>
                  <button
                     type="submit"
                     className="messageTemplates__btn messageTemplates__btn--primary"
                     disabled={busyId != null || newName.trim().length < 2}
                  >
                     {busyId === "new" ? "Se adaugă..." : "Adaugă"}
                  </button>
               </form>
            )}

            {feedback?.text && (
               <p className={feedback.ok ? "messageTemplates__ok" : "messageTemplates__error"}>
                  {feedback.text}
               </p>
            )}
         </div>
      </section>
   );
}
